import {
  CTCTooltip,
  affiliateLandingPageAnimation,
  individualLandingPageAnimation,
} from '@domains/kyc';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@latitude/accordion';
import { Icon } from '@latitude/icons';
import { Text } from '@latitude/text';
import { FORM_TYPE } from 'hooks/useFormType/useFormType';
import type { ReactNode } from 'react';

// oxlint-disable-next-line typescript/no-explicit-any -- Pre-existing usage; CEXP-20389
export const LANDING_PAGE_IMAGE: Record<FORM_TYPE, any> = {
  [FORM_TYPE.AFFILIATE]: affiliateLandingPageAnimation,
  [FORM_TYPE.INDIVIDUAL]: individualLandingPageAnimation,
  [FORM_TYPE.AFFILIATE_ADS]: affiliateLandingPageAnimation,
  [FORM_TYPE.INDIVIDUAL_ADS]: individualLandingPageAnimation,
  [FORM_TYPE.AFFILIATE_A1]: affiliateLandingPageAnimation,
  [FORM_TYPE.INDIVIDUAL_A1]: individualLandingPageAnimation,
  [FORM_TYPE.AFFILIATE_ADSD]: affiliateLandingPageAnimation,
  [FORM_TYPE.INDIVIDUAL_ADSD]: individualLandingPageAnimation,
  [FORM_TYPE.RFI]: affiliateLandingPageAnimation,
};

export const FORM_HEAD_TITLE = {
  [FORM_TYPE.AFFILIATE]:
    'Anchorage Digital Client Account Application - Start Form',
  [FORM_TYPE.AFFILIATE_ADS]:
    'Anchorage Digital Singapore Affiliate Account Application - Start Form',
  [FORM_TYPE.INDIVIDUAL]: 'Anchorage Digital Due Diligence - Start Form',
  [FORM_TYPE.INDIVIDUAL_ADS]:
    'Anchorage Digital Singapore Individual Account Application - Start Form',
  [FORM_TYPE.AFFILIATE_A1]:
    'Anchorage A1 Client Account Application - Start Form',
  [FORM_TYPE.INDIVIDUAL_A1]: 'Anchorage A1 Due Diligence - Start Form',
  [FORM_TYPE.AFFILIATE_ADSD]:
    'Anchorage Digital Swap Dealer Client Account Application - Start Form',
  [FORM_TYPE.INDIVIDUAL_ADSD]:
    'Anchorage Digital Swap Dealer Due Diligence - Start Form',
  [FORM_TYPE.RFI]: 'Anchorage RFI Application - Start Form',
};

export enum LANDING_PAGE_DRAWERS {
  ACCOUNT_SETUP = 'ACCOUNT_SETUP',
  EXPECTED_ACTIVITY = 'EXPECTED_ACTIVITY',
  ORGANIZATION_DETAILS = 'ORGANIZATION_DETAILS',
  ADS_ORGANIZATION_DETAILS = 'ADS_ORGANIZATION_DETAILS',
  VERIFY_YOUR_IDENTITY = 'VERIFY_YOUR_IDENTITY',
}

// A single component for the repeated `<ul><li><Text>...</Text></li></ul>`
// pattern used throughout these drawer contents.
const BulletList = ({ items }: { items: ReactNode[] }) => (
  <ul className="list-disc py-2.5 pl-5">
    {items.map((item, index) => (
      <li key={index}>
        <Text>{item}</Text>
      </li>
    ))}
  </ul>
);

export const getLandingPageCardsContent = (
  formType: FORM_TYPE,
  isAdsAuthorizedUser?: boolean,
  isArmadaApplication = false,
) => {
  switch (formType) {
    case FORM_TYPE.INDIVIDUAL_ADS:
      return [
        {
          icon: <Icon name="user" />,
          drawerID: undefined,
          title: 'Provide personal details',
          description:
            'To ensure legal documentation and regulatory compliance, provide personal details including your name, email address, date of birth, physical address, and passport',
        },
        ...(!isAdsAuthorizedUser
          ? [
              {
                icon: <Icon name="passport" />,
                drawerID: LANDING_PAGE_DRAWERS.VERIFY_YOUR_IDENTITY,
                title: 'Verify your identity',
                description:
                  "You'll need to upload a certified true copy of your passport and proof of address for identity verification. This helps us prevent fraud and ensures regulatory compliance.",
              },
            ]
          : []),
      ];
    case FORM_TYPE.AFFILIATE:
      return [
        {
          icon: <Icon name="building" />,
          drawerID: LANDING_PAGE_DRAWERS.ORGANIZATION_DETAILS,
          title: 'Provide organization details',
          description:
            "To meet legal, regulatory, and tax compliance requirements, provide your organization's name, business activities, principal place of business, and tax identification details",
        },
        {
          icon: <Icon name="user-plus" />,
          drawerID: LANDING_PAGE_DRAWERS.ACCOUNT_SETUP,
          title: 'Setup account',
          description: isArmadaApplication
            ? 'Add your authorized users and select your preferred contact details for receiving important communications'
            : 'Add at least 3 authorized users and select your preferred contact details for receiving important communications',
        },
        {
          icon: <Icon name="transferbrowser" />,
          drawerID: LANDING_PAGE_DRAWERS.EXPECTED_ACTIVITY,
          title: 'State expected activity',
          description:
            'Provide details about your anticipated activities to help us establish your transactional profile in compliance with regulatory requirements',
        },
      ].filter((item): item is NonNullable<typeof item> => item !== null);
    // ADSD shares the A1 landing content (ONMON-5268)
    case FORM_TYPE.AFFILIATE_A1:
    case FORM_TYPE.AFFILIATE_ADSD:
      return [
        {
          icon: <Icon name="building" />,
          drawerID: LANDING_PAGE_DRAWERS.ORGANIZATION_DETAILS,
          title: 'Provide organization details',
          description:
            "For legal and regulatory compliance, provide your organization's name, business activities, and principal place of business",
        },
      ];
    case FORM_TYPE.INDIVIDUAL:
    case FORM_TYPE.INDIVIDUAL_A1:
    case FORM_TYPE.INDIVIDUAL_ADSD:
      return [
        {
          icon: <Icon name="user" />,
          drawerID: undefined,
          title: 'Provide personal details',
          description:
            'To ensure legal documentation and regulatory compliance, provide personal details including your name, email address, date of birth, physical address, social security number, and a government issued ID',
        },
        {
          icon: <Icon name="passport" />,
          drawerID: undefined,
          title: 'Verify your identity',
          description:
            "You'll need to upload a photo ID for identity verification. This helps us prevent fraud and ensures regulatory compliance.",
        },
      ];
    case FORM_TYPE.RFI:
      return [
        {
          icon: <Icon name="check-verified" />,
          drawerID: undefined,
          title: 'Verify information',
          description: "Review and confirm your organization's details on file",
        },
        {
          icon: <Icon name="file-table" />,
          drawerID: undefined,
          title: 'Upload documentation',
          description:
            'Upload the documents needed for your due diligence and compliance review',
        },
      ];
    case FORM_TYPE.AFFILIATE_ADS:
      return [
        {
          icon: <Icon name="building" />,
          drawerID: LANDING_PAGE_DRAWERS.ADS_ORGANIZATION_DETAILS,
          title: 'Provide organization details',
          description:
            "To meet legal, regulatory, and tax compliance requirements, provide your organization's name, business activities, principal place of business, and tax identification details",
        },
        {
          icon: <Icon name="user-plus" />,
          drawerID: LANDING_PAGE_DRAWERS.ACCOUNT_SETUP,
          title: 'Setup account',
          description:
            'Add at least 3 authorized users and select your preferred contact details for receiving important communications',
        },
        {
          icon: <Icon name="transferbrowser" />,
          drawerID: LANDING_PAGE_DRAWERS.EXPECTED_ACTIVITY,
          title: 'State expected activity',
          description:
            'Provide details about your anticipated activities to help us establish your transactional profile in compliance with regulatory requirements',
        },
      ];
  }
};

export const getLandingPageDrawerContent = (
  id: LANDING_PAGE_DRAWERS,
  isA1Application: boolean,
  isAdsdApplication = false,
  isArmadaApplication = false,
) => {
  // ADSD shares the A1 drawer content
  const showA1Content = isA1Application || isAdsdApplication;
  switch (id) {
    case LANDING_PAGE_DRAWERS.ACCOUNT_SETUP:
      return {
        title: 'Adding authorized users',
        content: (
          <>
            <Text>
              For initial account setup, we need to add authorized users. You
              can modify these settings in more detail once you access the
              Anchorage Digital app.
            </Text>
            <Accordion
              size="small"
              collapsible
              fill="outlined"
              type="single"
              defaultValue="authorized-users"
            >
              <AccordionItem value="authorized-users">
                <AccordionTrigger title="Authorized users" />
                <AccordionContent>
                  <div className="flex flex-col gap-4">
                    {isArmadaApplication ? (
                      <>
                        <Text>
                          Add at least one authorized user from your
                          organization. Anchorage Agentic Banking does not use
                          quorum-based approvals, so a single authorized user is
                          enough, and you can add more at any time. Authorized
                          users have full administrative control by default.
                        </Text>
                        <Text>
                          An authorized user is an individual designated by the
                          client to perform transactions and manage the
                          organization in Anchorage Agentic Banking.
                        </Text>
                      </>
                    ) : (
                      <>
                        <Text>
                          To enable quorum-based approvals, you must add at
                          least 3 authorized users from your organization. These
                          users will have full administrative control by
                          default.
                        </Text>
                        <Text>
                          An authorized user is an individual designated by the
                          client to perform transactions and participate in
                          quorum actions within the Anchorage Digital app.
                        </Text>
                      </>
                    )}
                    <Text>
                      Authorized users may perform sensitive operations, such
                      as:
                    </Text>
                    <BulletList
                      items={[
                        'Creating vaults',
                        'Withdrawing assets',
                        'Interacting with dApps and smart contracts',
                        'Adding or removing users',
                        'Allowlisting addresses',
                        'Decrypting wallet keys',
                      ]}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion
              size="small"
              collapsible
              fill="outlined"
              type="single"
              defaultValue="document-delivery"
            >
              <AccordionItem value="document-delivery">
                <AccordionTrigger title="Document delivery" />
                <AccordionContent>
                  <div className="flex flex-col gap-4">
                    <Text>
                      Provide one or more email addresses to receive important
                      account notifications, including:
                    </Text>
                    <BulletList
                      items={[
                        'Invoices',
                        'KYC inquiries',
                        'Platform service updates',
                      ]}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </>
        ),
      };
    case LANDING_PAGE_DRAWERS.EXPECTED_ACTIVITY:
      return {
        title: 'What type of question will I need to answer?',
        content: (
          <>
            <Text>
              To understand your transactional profile and to ensure regulatory
              compliance, you will need to provide details on:
            </Text>
            <BulletList
              items={[
                'Expected digital asset and fiat transactions',
                'Anticipated account usage',
                'Transaction ranges and volumes',
                'Asset custody amounts',
                'Sources of funds',
              ]}
            />

            <Text>
              This information helps us maintain regulatory standards.
            </Text>
          </>
        ),
      };
    case LANDING_PAGE_DRAWERS.ORGANIZATION_DETAILS:
      return {
        title: 'What information should I gather?',
        content: (
          <div className="flex flex-col gap-4 overflow-scroll">
            <Accordion
              size="small"
              collapsible
              fill="outlined"
              type="single"
              defaultValue="basic-business-information"
            >
              <AccordionItem value="basic-business-information">
                <AccordionTrigger
                  title="Basic business information"
                  subtitle="Required for all clients"
                />
                <AccordionContent>
                  <div className="flex flex-col gap-4">
                    <Text>
                      {showA1Content
                        ? "Provide essential information including your organization's name, business activities, and primary place of business for legal documentation and regulatory compliance."
                        : `Provide your organization's name, business activities, primary
                  place of business, and tax identification details. This
                  information is required for compliance and helps prevent
                  unnecessary backup withholding.`}
                    </Text>
                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold">Required documents</Text>

                      <BulletList
                        items={[
                          <>
                            <span className="font-bold">
                              {`${'Proof of address:'} `}
                            </span>
                            If available, submit a document showing your
                            principal place of business, such as a utility bill
                            or recent statement (issued within the last 90
                            days).
                          </>,
                        ]}
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion
              size="small"
              collapsible
              fill="outlined"
              type="single"
              defaultValue="business-operation-details"
            >
              <AccordionItem value="business-operation-details">
                <AccordionTrigger
                  title="Business operation details"
                  subtitle="Required for all clients"
                />
                <AccordionContent>
                  <div className="flex flex-col gap-4">
                    <Text>
                      Depending on your business type, we will request specific
                      details tailored to your operations. This may include
                      information about the products or services you offer, your
                      licensing status, and the industries you are involved in.
                    </Text>

                    <Text variant="bodyBold">Required documents</Text>

                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold">For all clients</Text>

                      <BulletList
                        items={[
                          <>
                            <span className="font-bold">
                              {`${'Constitutional document:'} `}
                            </span>
                            {
                              'e.g., Articles of Incorporation/Organization, Bylaws, Operating Agreement, Partnership Agreement (not applicable to funds or high-net-worth individuals).'
                            }
                          </>,
                          <>
                            <span className="font-bold">
                              {`${'Additional documentation (optional):'} `}
                            </span>
                            Provide any other documentation you believe is
                            relevant for your account application.
                          </>,
                        ]}
                      />
                    </div>

                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold">For funds</Text>

                      <BulletList
                        items={[
                          <>
                            <span className="font-bold">
                              {`${'Offering document:'} `}
                            </span>
                            A legal document that provides comprehensive details
                            about a securities offering. It includes financial
                            statements, management information, business model,
                            risks, and terms.
                          </>,
                          <>
                            <span className="font-bold">
                              {`${'Fund structure chart:'} `}
                            </span>
                            A chart that visually represents the relationships
                            and flow of funds among entities involved in the
                            fund.
                          </>,
                        ]}
                      />
                    </div>

                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold">For trusts</Text>

                      <BulletList
                        items={[
                          <>
                            <span className="font-bold">
                              {`${'Trust agreement:'} `}
                            </span>
                            A financial instrument that represents an investor’s
                            interest in a trust. Typically backed by assets such
                            as real estate or loans, it entitles the holder to a
                            share of the income generated from those assets.
                          </>,
                        ]}
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion
              size="small"
              collapsible
              fill="outlined"
              type="single"
              defaultValue="anti-money-laundering-program"
            >
              <AccordionItem value="anti-money-laundering-program">
                <AccordionTrigger title="Anti-money laundering program" />
                <Text
                  variant="captionRegular"
                  className="mx-4 pb-2 text-color-ref-text-muted"
                >
                  {
                    'Only applicable to financial institutions, investment funds, and exchange-traded products or funds (ETP or ETF)'
                  }
                </Text>
                <AccordionContent>
                  <div className="flex flex-col gap-4">
                    <Text>
                      {
                        'Specify if you maintain an anti-money laundering (AML) program. If you do, indicate which of the following aspects it covers. If not, provide additional context explaining why.'
                      }
                    </Text>
                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold">
                        Anti-money laundering program details
                      </Text>

                      <BulletList
                        items={[
                          'Designated individual(s) responsible for implementing and monitoring the program.',
                          'Policies, procedures, and internal controls to facilitate ongoing compliance with AML laws and regulations.',
                          'Ongoing screening of investors, customers, and transactions against OFAC sanctions and other watchlists.',
                          'Ongoing screening of investors and customers for Politically Exposed Persons.',
                          'Policies and procedures for conducting ongoing customer due diligence, including customer identification and beneficial ownership verification.',
                          'Policies and procedures to detect and report suspicious activity.',
                          'Periodic independent testing of the program.',
                          'Ongoing AML and sanctions training for appropriate personnel.',
                          'Policies and procedures to retain required records.',
                        ]}
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion
              size="small"
              collapsible
              fill="outlined"
              type="single"
              defaultValue="ownership-information"
            >
              <AccordionItem value="ownership-information">
                <AccordionTrigger title="Ownership information" />
                <Text
                  variant="captionRegular"
                  className="mx-4 pb-2 text-color-ref-text-muted"
                >
                  Only applicable for institutions not exempt from beneficial
                  ownership requirements
                </Text>
                <AccordionContent>
                  <div className="flex flex-col gap-4">
                    <Text>
                      {
                        'To assist in combating financial crime, federal regulations require Anchorage Digital to verify and record information about key individuals who own or control your organization (i.e., the beneficial owners).'
                      }
                    </Text>
                    <Text>
                      This requires you to provide personal information for
                      individuals who qualify as Beneficial Owners and/or
                      Control Persons.
                    </Text>

                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold">
                        What information do I need to provide?
                      </Text>
                      <Text>
                        You must provide the name, address, date of birth,
                        government issued ID number, and photo ID for:
                      </Text>
                      <BulletList
                        items={[
                          'Each individual, if any, who, directly or indirectly, through any contract, arrangement, understanding, relationship or otherwise, owns 25% or more of the outstanding equity shares of the legal entity.',
                          'An individual with significant responsibility for managing the legal entity, such as an executive officer or senior officer or any other individual who regularly performs similar functions.',
                        ]}
                      />
                    </div>

                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold">Required documents</Text>
                      <BulletList
                        items={[
                          <>
                            <span className="font-bold">{`${'ID:'} `}</span>
                            {
                              "You'll need to upload an ID for identity verification of each beneficial owner or control person."
                            }
                          </>,
                          <>
                            <span className="font-bold">
                              {`${'Ownership chart:'} `}
                            </span>
                            {
                              'If there are intermediary layers of ownership between your organization and the ultimate beneficial owner(s), we will also ask you to provide an ownership chart.'
                            }
                          </>,
                        ]}
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            {showA1Content && (
              <Accordion
                size="small"
                collapsible
                fill="outlined"
                type="single"
                defaultValue="contact-preferences"
              >
                <AccordionItem value="contact-preferences">
                  <AccordionTrigger title="Contact preferences" />
                  <Text
                    variant="captionRegular"
                    color="muted"
                    className="mx-4 pb-2"
                  >
                    Required for all clients
                  </Text>
                  <AccordionContent>
                    <div className="flex flex-col gap-4">
                      <Text>
                        Enter the contact information for individuals who should
                        receive key documents and those we can reach out to for
                        trading-related questions
                      </Text>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
          </div>
        ),
      };
    case LANDING_PAGE_DRAWERS.ADS_ORGANIZATION_DETAILS:
      return {
        title: 'What information should I gather?',
        content: (
          <div className="flex flex-col gap-4 overflow-scroll">
            <Accordion
              size="small"
              collapsible
              fill="outlined"
              type="single"
              defaultValue="ads-basic-business-information"
            >
              <AccordionItem value="ads-basic-business-information">
                <AccordionTrigger
                  title="Basic business information"
                  subtitle="Required for all clients"
                />
                <AccordionContent>
                  <div className="flex flex-col gap-4">
                    <Text>
                      {`Provide your organization's name, business activities, primary
                  place of business, and tax identification details. This
                  information is required for compliance and helps prevent
                  unnecessary backup withholding.`}
                    </Text>
                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold">Required documents</Text>

                      <BulletList
                        items={[
                          <>
                            <span className="font-bold">
                              {`${'Proof of address:'} `}
                            </span>
                            If available, submit a certified true copy (CTC) of
                            a document showing your principal place of business,
                            such as a utility bill or recent statement (issued
                            within the last 90 days).
                          </>,
                        ]}
                      />
                    </div>

                    <CTCTooltip />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion
              size="small"
              collapsible
              fill="outlined"
              type="single"
              defaultValue="ads-business-operation-details"
            >
              <AccordionItem value="ads-business-operation-details">
                <AccordionTrigger
                  title="Business operation details"
                  subtitle="Required for all clients"
                />
                <AccordionContent>
                  <div className="flex flex-col gap-4">
                    <Text>
                      Depending on your business type, we will request specific
                      details tailored to your operations. This may include
                      information about the products or services you offer, your
                      licensing status, and the industries you are involved in.
                    </Text>

                    <Text variant="bodyBold">Required documents</Text>

                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold">For all clients</Text>

                      <BulletList
                        items={[
                          <>
                            <span className="font-bold">
                              {`${'Constitutional document:'} `}
                            </span>
                            {
                              'e.g., Articles of Incorporation/Organization, Bylaws, Operating Agreement, Partnership Agreement (not applicable to funds or high-net-worth individuals).'
                            }
                          </>,
                          <>
                            <span className="font-bold">
                              {`${'Additional documentation (optional):'} `}
                            </span>
                            Provide any other documentation you believe is
                            relevant for your account application.
                          </>,
                        ]}
                      />
                    </div>

                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold">For funds</Text>

                      <BulletList
                        items={[
                          <>
                            <span className="font-bold">
                              {`${'Offering document:'} `}
                            </span>
                            A legal document that provides comprehensive details
                            about a securities offering. It includes financial
                            statements, management information, business model,
                            risks, and terms.
                          </>,
                          <>
                            <span className="font-bold">
                              {`${'Fund structure chart:'} `}
                            </span>
                            A chart that visually represents the relationships
                            and flow of funds among entities involved in the
                            fund.
                          </>,
                        ]}
                      />
                    </div>

                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold">For trusts</Text>

                      <BulletList
                        items={[
                          <>
                            <span className="font-bold">
                              {`${'Trust agreement:'} `}
                            </span>
                            A financial instrument that represents an investor’s
                            interest in a trust. Typically backed by assets such
                            as real estate or loans, it entitles the holder to a
                            share of the income generated from those assets.
                          </>,
                        ]}
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion
              size="small"
              collapsible
              fill="outlined"
              type="single"
              defaultValue="ads-anti-money-laundering-program"
            >
              <AccordionItem value="ads-anti-money-laundering-program">
                <AccordionTrigger title="Anti-money laundering program" />
                <Text
                  variant="captionRegular"
                  className="mx-4 pb-2 text-color-ref-text-muted"
                >
                  {
                    'Only applicable to financial institutions, investment funds, and exchange-traded products or funds (ETP or ETF)'
                  }
                </Text>
                <AccordionContent>
                  <div className="flex flex-col gap-4">
                    <Text>
                      {
                        'Specify if you maintain an anti-money laundering (AML) program. If you do, indicate which of the following aspects it covers. If not, provide additional context explaining why.'
                      }
                    </Text>
                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold">
                        Anti-money laundering program details
                      </Text>

                      <BulletList
                        items={[
                          'Designated individual(s) responsible for implementing and monitoring the program.',
                          'Policies, procedures, and internal controls to facilitate ongoing compliance with AML laws and regulations.',
                          'Ongoing screening of investors, customers, and transactions against OFAC sanctions and other watchlists.',
                          'Ongoing screening of investors and customers for Politically Exposed Persons.',
                          'Policies and procedures for conducting ongoing customer due diligence, including customer identification and beneficial ownership verification.',
                          'Policies and procedures to detect and report suspicious activity.',
                          'Periodic independent testing of the program.',
                          'Ongoing AML and sanctions training for appropriate personnel.',
                          'Policies and procedures to retain required records.',
                        ]}
                      />
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion
              size="small"
              collapsible
              fill="outlined"
              type="single"
              defaultValue="ads-ownership-information"
            >
              <AccordionItem value="ads-ownership-information">
                <AccordionTrigger title="Ownership information" />
                <Text
                  variant="captionRegular"
                  className="mx-4 pb-2 text-color-ref-text-muted"
                >
                  Only applicable for institutions not exempt from beneficial
                  ownership requirements
                </Text>
                <AccordionContent>
                  <div className="flex flex-col gap-4">
                    <Text>
                      {
                        'To assist in combating financial crime, federal regulations require Anchorage Digital to verify and record information about key individuals who own or control your organization (i.e., the beneficial owners).'
                      }
                    </Text>
                    <Text>
                      This requires you to provide personal information for
                      individuals who qualify as Beneficial Owners and/or
                      Control Persons.
                    </Text>

                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold">
                        What information do I need to provide?
                      </Text>
                      <Text>
                        You must provide the name, alias, residential address,
                        date of birth, country of citizenship, US taxpayer ID
                        number or government issued ID number, and passport for:
                      </Text>
                      <BulletList
                        items={[
                          'Each individual, if any, who owns, directly or indirectly, more than 25 percent of the equity interests of the legal entity customer (e.g., each natural person that owns more than 25 percent of the shares of a corporation).',
                          'All individuals who exercise control of the legal entity customer through other means.',
                        ]}
                      />
                    </div>

                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold">Required documents</Text>
                      <BulletList
                        items={[
                          <>
                            <span className="font-bold">{`${'Passport:'} `}</span>
                            {
                              "You'll need to upload a certified true copy (CTC) of a passport for identity verification of each beneficial owner, connected party, or authorized signatory."
                            }
                          </>,
                          <>
                            <span className="font-bold">
                              {`${'Ownership chart:'} `}
                            </span>
                            {
                              'If there are intermediary layers of ownership between your organization and the ultimate beneficial owner(s), we will also ask you to provide an ownership chart.'
                            }
                          </>,
                        ]}
                      />
                    </div>
                    <CTCTooltip />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <Accordion
              size="small"
              collapsible
              fill="outlined"
              type="single"
              defaultValue="ads-accredited-investor"
            >
              <AccordionItem value="ads-accredited-investor">
                <AccordionTrigger title="Accredited investor" />
                <Text
                  variant="captionRegular"
                  className="mx-4 pb-2 text-color-ref-text-muted"
                >
                  Only applicable for high-net-worth individuals
                </Text>
                <AccordionContent>
                  <div className="flex flex-col gap-4">
                    <Text>
                      To apply for an account as a high-net-worth individual,
                      you must attest to your current status as an accredited
                      investor.
                    </Text>

                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold">
                        What information do I need to provide?
                      </Text>
                      <Text>
                        You must attest to your status as an accredited investor
                        by selecting the criteria under which you qualify:
                      </Text>
                      <BulletList
                        items={[
                          'Personal Income: Exceeded S$300,000 in the past 12 months.',
                          'Net Personal Assets: Exceed S$2 million.',
                          'Net Financial Assets: Exceed S$1 million after liabilities.',
                        ]}
                      />
                    </div>

                    <div className="flex flex-col gap-0.5">
                      <Text variant="bodyBold">Required documents</Text>
                      <Text>
                        To verify your accredited investor status, provide a
                        certified true copy (CTC) of the following documents
                        based on the applicable criteria.
                      </Text>
                      <BulletList
                        items={[
                          <>
                            <span className="font-bold">{`${'Personal Income:'} `}</span>
                            {
                              'Pay slip (dated within the last three months), employment contract stating your position and income, or latest tax statement.'
                            }
                          </>,
                          <>
                            <span className="font-bold">
                              {`${'Net Personal Assets:'} `}
                            </span>
                            {
                              'Latest bank account statement, pension statement, statement of holdings from a brokerage firm, or the title deed of your primary residence.'
                            }
                          </>,
                          <>
                            <span className="font-bold">
                              {`${'Net Financial Assets:'} `}
                            </span>
                            {
                              'Latest bank account statement or pension statement, dated within the last three months.'
                            }
                          </>,
                        ]}
                      />
                    </div>
                    <CTCTooltip />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        ),
      };
    case LANDING_PAGE_DRAWERS.VERIFY_YOUR_IDENTITY:
      return {
        title: 'Verify your identity',
        content: (
          <>
            <Text>
              {
                "To help us prevent fraud and ensure regulatory compliance, you'll need to provide the following documents:"
              }
            </Text>
            <BulletList
              items={[
                <>
                  <span className="font-bold">{`${'Passport:'} `}</span>
                  {
                    "You'll need to upload a certified true copy of a passport for identity verification of each beneficial owner, connected party or authorized signatory."
                  }
                </>,
                <>
                  <span className="font-bold">{`${'Proof of address:'} `}</span>
                  {
                    'Submit a certified true copy of a document showing your residential address, such as a utility bill or recent statement (issued within the last 90 days).'
                  }
                </>,
              ]}
            />

            <div>
              <CTCTooltip />
            </div>
          </>
        ),
      };
  }
};

export const HELP_EMAIL = 'accountexecutive@anchorage.com';

export const openHelpEmail = (formType?: string, helpEmail = HELP_EMAIL) => {
  let applicationText;
  switch (formType) {
    case FORM_TYPE.AFFILIATE:
    case FORM_TYPE.AFFILIATE_ADS:
    case FORM_TYPE.AFFILIATE_A1:
    case FORM_TYPE.AFFILIATE_ADSD:
      applicationText = 'the Affiliate Form';
      break;
    case FORM_TYPE.INDIVIDUAL:
    case FORM_TYPE.INDIVIDUAL_ADS:
    case FORM_TYPE.INDIVIDUAL_A1:
    case FORM_TYPE.INDIVIDUAL_ADSD:
      applicationText = 'my KYC Form';
      break;
    default:
      applicationText = 'the onboarding application';
      break;
  }
  const subject = `subject=Onboarding Help: Need assistance with ${applicationText}`;
  const message =
    "&body=Anchorage Digital team,\n  I'm having an issue with...";

  window.open(`mailto:${helpEmail}${encodeURI(`?${subject}${message}`)}`);
};
